import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, RefreshCw, AlertTriangle, Lock, ExternalLink, BarChart3,
  Loader2, CheckCircle, Activity, TrendingUp, Info,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getMyTrackRecord, refreshMyTrackRecord, getExchangeConnections } from '../services/api';
import TrackRecordBadge from '../components/TrackRecordBadge';
import { InfoTip } from '../components/ui';

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function SellerVerifyPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [cooldownNotice, setCooldownNotice] = useState(null);
  const [exchanges, setExchanges] = useState([]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [tr, conns] = await Promise.all([
        getMyTrackRecord(),
        getExchangeConnections().catch(() => null),
      ]);
      setData(tr?.data || tr);
      const list = conns?.exchanges?.filter(e => e.connected) || [];
      setExchanges(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRefresh() {
    setRefreshing(true);
    setError(null);
    setCooldownNotice(null);
    try {
      await refreshMyTrackRecord();
      await load();
    } catch (err) {
      // 429 cooldown is NOT a failure — surface it as an info notice, not red error
      if (err?.httpStatus === 429) {
        const m = (err.message || '').match(/in (\d+) minute/);
        const min = m ? m[1] : null;
        setCooldownNotice(
          min
            ? `Already up to date — you can refresh again in ${min} minute${min === '1' ? '' : 's'}. Track-record refreshes are limited to once per hour because pulling exchange history is expensive.`
            : 'Already up to date — refreshes are limited to once per hour.'
        );
      } else {
        setError(err.message);
      }
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const record = data?.record;
  const summary = record?.summary;
  const isPro = data?.canPublish || user?.role === 'admin';
  const hasExchanges = exchanges.length > 0;
  const qualifies = summary?.qualifiesForBadge;

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-7 h-7 text-emerald-400" />
          <h1 className="text-2xl font-bold text-white">Verified Track Record</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/30">Pro+</span>
        </div>
        <p className="text-sm text-gray-400">
          Prove your trading history with on-exchange data. Verified sellers get a green badge on marketplace listings and Copy Trading leaderboards — the same numbers your exchange records, no marketing claims.
        </p>
      </header>

      {!isPro && (
        <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-5 flex items-start gap-3">
          <Lock className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-violet-400 text-sm font-bold">Pro tier required to publish</p>
            <p className="text-xs text-gray-400 mt-1">
              Anyone can view their own track record stats, but publishing strategies + earning revenue share requires Pro tier or higher.
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 mt-2 font-medium">
              See Pro tier features <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}

      {/* No exchanges connected — guidance */}
      {!hasExchanges && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-400 text-sm font-bold">No exchanges connected</p>
            <p className="text-xs text-gray-400 mt-1">
              Connect at least one CEX in Settings → Connections so we can pull your real trade history. We use read-only API permissions — never withdraw, never trade on your behalf.
            </p>
            <Link to="/settings?tab=connections" className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 mt-2 font-medium">
              Go to Connections <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}

      {/* Main panel: status + refresh */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-white">Your verification status</h2>
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
              Computed from <span className="text-emerald-400">real CCXT trade history</span> across your connected exchanges. {data?.windowDays}-day window. Refreshes once per hour.
              <InfoTip side="bottom">Read-only API keys, never withdrawal. 30-day window keeps stats relevant. Cooldown protects your exchange API quota — 429 is rendered as info, not failure.</InfoTip>
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || !hasExchanges}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-dark-600 disabled:text-gray-500 text-black text-sm font-semibold transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Computing…' : record ? 'Refresh' : 'Run First Verification'}
          </button>
        </div>

        {error && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs mb-4 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {cooldownNotice && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-300 text-xs mb-4 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{cooldownNotice}</span>
          </div>
        )}

        {!record && !error && (
          <div className="bg-dark-700/30 rounded-lg p-8 text-center">
            <BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-2" />
            <p className="text-white font-medium mb-1">No track record on file yet</p>
            <p className="text-xs text-gray-500">
              Click <span className="text-emerald-400">Run First Verification</span> to pull your trades from connected exchanges and compute your stats.
            </p>
          </div>
        )}

        {record && (
          <>
            {/* Top-level summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Stat label="Round-trips" value={summary.roundTrips} sub={`Min ${data.minRoundTrips} for badge`} />
              <Stat label="Net P&L" value={`${summary.netPnl >= 0 ? '+' : ''}$${formatNum(summary.netPnl, 2)}`} accent={summary.netPnl > 0 ? 'emerald' : summary.netPnl < 0 ? 'red' : undefined} sub={`Realized $${formatNum(summary.realizedPnl, 0)}, fees $${formatNum(summary.totalFees, 0)}`} />
              <Stat label="Win rate" value={`${formatNum(summary.winRate, 1)}%`} sub={`${summary.winningTrips}W / ${summary.losingTrips}L`} />
              <Stat label="Max drawdown" value={`${formatNum(summary.maxDrawdownPct, 1)}%`} accent="red" sub={`$${formatNum(summary.maxDrawdownDollar, 0)} from peak`} />
            </div>

            {/* Badge preview */}
            <div className="border-t border-dark-700/50 pt-4 mb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Public badge preview (this is what buyers will see)</p>
              <TrackRecordBadge
                trackRecord={qualifies ? {
                  verified: true,
                  windowDays: data.windowDays,
                  computedAt: record.computedAt,
                  summary: {
                    netPnl: summary.netPnl,
                    realizedPnl: summary.realizedPnl,
                    roundTrips: summary.roundTrips,
                    winRate: summary.winRate,
                    maxDrawdownPct: summary.maxDrawdownPct,
                    totalVolume: summary.totalVolume,
                  },
                  exchangeCount: record.exchangesContributing?.length || 0,
                } : { verified: false, reason: `Need ${data.minRoundTrips - summary.roundTrips} more round-trip${data.minRoundTrips - summary.roundTrips === 1 ? '' : 's'}` }}
                mode="full"
              />
            </div>

            {/* Exchanges contributing */}
            <div className="border-t border-dark-700/50 pt-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Exchanges contributing</p>
              {record.exchangesContributing.length === 0 ? (
                <p className="text-xs text-gray-600 italic">No exchanges contributed any trades in the {data.windowDays}-day window.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {record.exchangesContributing.map(e => (
                    <span key={e.exchangeId} className="text-xs bg-dark-700/40 border border-dark-600/40 rounded-lg px-3 py-1.5 text-gray-300 font-mono">
                      {e.exchangeId} · {e.tradeCount} trades
                    </span>
                  ))}
                </div>
              )}
              {record.fetchErrors?.length > 0 && (
                <div className="mt-3 text-xs text-amber-400 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {record.fetchErrors.length} exchange{record.fetchErrors.length === 1 ? '' : 's'} failed to fetch:
                    <ul className="text-amber-300 mt-1 space-y-0.5">
                      {record.fetchErrors.slice(0, 3).map((e, i) => <li key={i} className="text-[10px]">• {e}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <p className="text-[10px] text-gray-600 mt-4 flex items-start gap-1">
              <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
              Last computed {new Date(record.computedAt).toLocaleString()}. Refresh available once per hour. All data comes from your real exchange trade history — no estimates, no simulation.
            </p>
          </>
        )}
      </div>

      {/* Why verify */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" /> Why verify?
        </h3>
        <ul className="space-y-2 text-xs text-gray-400">
          <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /><span><span className="text-white font-medium">Build trust:</span> Buyers see your real win rate, drawdown, and round-trip count — not your marketing claim. The numbers come straight from your exchange's trade history.</span></li>
          <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /><span><span className="text-white font-medium">Stand out in the marketplace:</span> Verified strategies show a green badge with live stats; unverified ones get an amber chip. Buyers can filter to verified-only with one toggle.</span></li>
          <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /><span><span className="text-white font-medium">Earn revenue share:</span> Strategy creators earn <span className="text-emerald-400 font-mono">5%</span> of subscriber profits and copy traders earn <span className="text-emerald-400 font-mono">10%</span> of their copiers' profitable round-trips. A verified track record makes buyers and copiers more likely to pick you.</span></li>
          <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /><span><span className="text-white font-medium">No key risk:</span> We use read-only API permissions. Never request withdrawal access. Never store keys in plaintext.</span></li>
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className="bg-dark-700/30 rounded-xl p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}
